import {
  Button,
  ButtonTargetKind,
  ButtonSize,
  ButtonRole,
} from "@components/Button";
import { ErrorMessage } from "@components/ErrorMessage";
import type { CSSInterpolation } from "@emotion/css";
import styled from "@emotion/styled";
import React, { useState } from "react";

import { UserResponse } from "@every.org/common/src/codecs/entities";
import { getUserFullNameOrPlaceholder } from "@every.org/common/src/helpers/username";
import { approveFollowRouteSpec } from "@every.org/common/src/routes/follow";

import { addUser } from "src/context/UsersContext/actions";
import { spacing } from "src/theme/spacing";
import { queryApi } from "src/utility/apiClient";

const AcceptFollowContainer = styled.div``;

const AcceptFollowContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;

  > *:not(:last-child) {
    margin-right: ${spacing.xs};
  }
`;

enum Status {
  AWAITING_USER_INPUT,
  SUBMITTING,
}

interface AcceptFollowProps {
  user: UserResponse;
  showRequestMessage?: boolean;
  onAccepted?: () => void;
  contentCss?: CSSInterpolation | CSSInterpolation[];
}
export const AcceptFollow: React.FCC<AcceptFollowProps> = ({
  user,
  showRequestMessage,
  onAccepted,
  contentCss,
}) => {
  const [status, setStatus] = useState(Status.AWAITING_USER_INPUT);
  const [error, setError] = useState<string>();

  const respond = async (approve: boolean) => {
    try {
      const result = await queryApi(approveFollowRouteSpec, {
        body: { fromId: user.id, approve },
        routeTokens: {},
        queryParams: {},
      });
      addUser(result.fromUser);
      if (approve && onAccepted) {
        onAccepted();
      }
    } catch (e) {
      setStatus(Status.AWAITING_USER_INPUT);
      setError("Could not accept follow request. Please try again.");
    }
  };

  return (
    <AcceptFollowContainer>
      <AcceptFollowContent css={contentCss}>
        {showRequestMessage && (
          <p>{getUserFullNameOrPlaceholder(user)} would like to follow you.</p>
        )}
        <Button
          data-tname="userProfileFollowApprove"
          role={ButtonRole.PRIMARY}
          size={ButtonSize.SMALL}
          disabled={status !== Status.AWAITING_USER_INPUT}
          onClick={{
            kind: ButtonTargetKind.FUNCTION,
            action: () => {
              respond(true);
            },
          }}
          aria-label="Accept Follow"
        >
          Accept
        </Button>
        <Button
          data-tname="userProfileFollowIgnore"
          size={ButtonSize.SMALL}
          role={ButtonRole.SECONDARY}
          disabled={status !== Status.AWAITING_USER_INPUT}
          onClick={{
            kind: ButtonTargetKind.FUNCTION,
            action: () => {
              respond(false);
            },
          }}
          aria-label="Ignore Follow"
        >
          Ignore
        </Button>
      </AcceptFollowContent>
      {error && <ErrorMessage text={error} />}
    </AcceptFollowContainer>
  );
};
